import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, doc, setDoc, deleteDoc, addDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, Pencil, Trash2, GripVertical, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface NavSubItem {
  title: {
    ar: string;
    en: string;
  };
  hidden?: boolean;
}

interface NavItem {
  id?: string;
  title: {
    ar: string;
    en: string;
  };
  dropdown: boolean;
  items?: NavSubItem[];
  order: number;
  parentMenu?: string; // ID of the parent menu
  hidden?: boolean;
}

export function NavigationSection() {
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<NavItem | null>(null);
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);

  useEffect(() => {
    fetchNavItems();
  }, []);

  const fetchNavItems = async () => {
    try {
      const navQuery = query(collection(db, 'landing_navigation'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(navQuery);

      const items = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as NavItem[];

      setNavItems(items);
    } catch (error) {
      console.error('Error fetching navigation items:', error);
      toast.error('حدث خطأ أثناء تحميل عناصر القائمة');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItem = async (item: NavItem) => {
    try {
      if (item.id) {
        // Update existing item
        await updateDoc(doc(db, 'landing_navigation', item.id), {
          title: item.title,
          dropdown: item.dropdown,
          items: item.items || [],
          order: item.order,
          hidden: item.hidden || false,
          updatedAt: new Date()
        });
        toast.success('تم تحديث عنصر القائمة بنجاح');
      } else {
        // Add new item
        await addDoc(collection(db, 'landing_navigation'), {
          ...item,
          hidden: item.hidden || false,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        toast.success('تم إضافة عنصر القائمة بنجاح');
      }
      fetchNavItems();
      setEditModalOpen(false);
      setAddModalOpen(false);
      setCurrentItem(null);
    } catch (error) {
      console.error('Error saving navigation item:', error);
      toast.error('حدث خطأ أثناء حفظ عنصر القائمة');
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا العنصر؟')) return;

    try {
      await deleteDoc(doc(db, 'landing_navigation', id));
      toast.success('تم حذف عنصر القائمة بنجاح');
      fetchNavItems();
    } catch (error) {
      console.error('Error deleting navigation item:', error);
      toast.error('حدث خطأ أثناء حذف عنصر القائمة');
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  // Separate menus and collect all sub-items from menus
  const menus = navItems.filter(item => item.dropdown);
  
  // Get all sub-items from all menus (or filtered by selected menu)
  const allSubItems: (NavSubItem & { parentMenuId?: string; parentMenuTitle?: string })[] = [];
  const menusToProcess = selectedMenuId ? menus.filter(m => m.id === selectedMenuId) : menus;
  
  menusToProcess.forEach(menu => {
    if (menu.items && menu.items.length > 0) {
      menu.items.forEach(subItem => {
        allSubItems.push({
          ...subItem,
          parentMenuId: menu.id,
          parentMenuTitle: menu.title
        });
      });
    }
  });

  const renderNavItem = (item: NavItem) => (
    <div 
      key={item.id} 
      className={`border rounded-lg overflow-hidden cursor-pointer transition-all group ${
        selectedMenuId === item.id ? 'ring-2 ring-primary shadow-lg' : ''
      } ${item.hidden ? 'opacity-60' : ''}`}
      onClick={() => setSelectedMenuId(selectedMenuId === item.id ? null : item.id || null)}
    >
      <div className="flex items-center gap-3 p-4 bg-secondary/30 hover:bg-secondary/50 transition-colors">
        <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" onClick={(e) => e.stopPropagation()} />
        
        <div className="flex-1">
          <div className="font-semibold">
            {typeof item.title === 'string' ? item.title : item.title.ar}
          </div>
          <div className="text-sm text-muted-foreground">
            {typeof item.title === 'string' ? '' : item.title.en}
          </div>
        </div>

        <div className={`flex gap-1 ${item.hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => {
              setCurrentItem(item);
              setEditModalOpen(true);
            }}
            className="p-2 hover:bg-blue-500/10 rounded-md transition-colors"
            title="تعديل"
          >
            <Pencil className="h-4 w-4 text-muted-foreground" />
          </button>
          <button
            onClick={async () => {
              if (item.id) {
                await handleSaveItem({
                  ...item,
                  hidden: !item.hidden
                });
              }
            }}
            className={`p-2 rounded-md transition-colors ${
              item.hidden 
                ? 'bg-[#7dda55]/10 hover:bg-[#7dda55]/20' 
                : 'hover:bg-[#7dda55]/10'
            }`}
            title={item.hidden ? 'إظهار' : 'إخفاء'}
          >
            <EyeOff className="h-4 w-4 text-[#7dda55]" />
          </button>
          <button
            onClick={() => item.id && handleDeleteItem(item.id)}
            className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
            title="حذف"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Items Column - 2/3 width */}
      <Card className="p-6 lg:col-span-2">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold">العناصر</h3>
            <p className="text-sm text-muted-foreground">
              {selectedMenuId 
                ? `عناصر قائمة: ${(() => {
                    const menu = menus.find(m => m.id === selectedMenuId);
                    if (!menu) return '';
                    return typeof menu.title === 'string' ? menu.title : menu.title.ar;
                  })()}`
                : 'اختر قائمة من اليمين'
              }
            </p>
          </div>
          <div className="flex gap-2">
            {selectedMenuId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedMenuId(null)}
                className="gap-2"
              >
                إلغاء التحديد
              </Button>
            )}
            {selectedMenuId && (
              <Button
                onClick={() => {
                  setCurrentItem({
                    title: '',
                    dropdown: false,
                    order: navItems.length,
                    items: [],
                    parentMenu: selectedMenuId || undefined
                  });
                  setAddModalOpen(true);
                }}
                size="sm"
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                إضافة
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
              {!selectedMenuId ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="text-lg font-medium mb-2">اختر قائمة لعرض عناصرها</p>
                  <p className="text-sm">انقر على أي قائمة من القائمة اليمنى</p>
                </div>
              ) : allSubItems.length > 0 ? (
                allSubItems.map((subItem, index) => (
                  <div key={index} className={`border rounded-lg overflow-hidden group ${subItem.hidden ? 'opacity-60' : ''}`}>
                    <div className="flex items-center gap-3 p-4 bg-secondary/50 hover:bg-secondary/70 transition-colors">
                      <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                      
                      <div className="flex-1">
                        <div className="font-semibold">
                          {typeof subItem.title === 'string' ? subItem.title : subItem.title.ar}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {typeof subItem.title === 'string' ? '' : subItem.title.en}
                        </div>
                      </div>

                      <div className={`flex gap-1 ${subItem.hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                        <button
                          onClick={() => {
                            const parentMenu = menus.find(m => m.id === subItem.parentMenuId);
                            if (parentMenu) {
                              setCurrentItem({
                                title: subItem.title,
                                dropdown: false,
                                order: 0,
                                items: [],
                                parentMenu: subItem.parentMenuId
                              });
                              setEditModalOpen(true);
                            }
                          }}
                          className="p-2 hover:bg-blue-500/10 rounded-md transition-colors"
                          title="تعديل"
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </button>
                        <button
                          onClick={async () => {
                            const parentMenu = menus.find(m => m.id === subItem.parentMenuId);
                            if (parentMenu && parentMenu.id) {
                              const updatedItems = (parentMenu.items || []).map(
                                item => {
                                  const itemTitleAr = typeof item.title === 'string' ? item.title : item.title.ar;
                                  const itemTitleEn = typeof item.title === 'string' ? '' : item.title.en;
                                  const subItemTitleAr = typeof subItem.title === 'string' ? subItem.title : subItem.title.ar;
                                  const subItemTitleEn = typeof subItem.title === 'string' ? '' : subItem.title.en;
                                  
                                  return (itemTitleAr === subItemTitleAr && itemTitleEn === subItemTitleEn)
                                    ? { ...item, hidden: !item.hidden }
                                    : item;
                                }
                              );
                              await handleSaveItem({
                                ...parentMenu,
                                items: updatedItems
                              });
                            }
                          }}
                          className={`p-2 rounded-md transition-colors ${
                            subItem.hidden 
                              ? 'bg-[#7dda55]/10 hover:bg-[#7dda55]/20' 
                              : 'hover:bg-[#7dda55]/10'
                          }`}
                          title={subItem.hidden ? 'إظهار' : 'إخفاء'}
                        >
                          <EyeOff className="h-4 w-4 text-[#7dda55]" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('هل أنت متأكد من حذف هذا العنصر؟')) {
                              const parentMenu = menus.find(m => m.id === subItem.parentMenuId);
                              if (parentMenu && parentMenu.id) {
                                const updatedItems = (parentMenu.items || []).filter(
                                  item => {
                                    const itemTitleAr = typeof item.title === 'string' ? item.title : item.title.ar;
                                    const itemTitleEn = typeof item.title === 'string' ? '' : item.title.en;
                                    const subItemTitleAr = typeof subItem.title === 'string' ? subItem.title : subItem.title.ar;
                                    const subItemTitleEn = typeof subItem.title === 'string' ? '' : subItem.title.en;
                                    
                                    return itemTitleAr !== subItemTitleAr || itemTitleEn !== subItemTitleEn;
                                  }
                                );
                                handleSaveItem({
                                  ...parentMenu,
                                  items: updatedItems
                                });
                              }
                            }
                          }}
                          className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  لا توجد عناصر فرعية في هذه القائمة
                </div>
              )}
        </div>
      </Card>

      {/* Menus Column - 1/3 width */}
      <Card className="p-6 lg:col-span-1">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold">القوائم</h3>
            <p className="text-sm text-muted-foreground">القوائم المنسدلة مع العناصر الفرعية</p>
          </div>
          <Button
            onClick={() => {
              setCurrentItem({
                title: '',
                dropdown: true,
                order: navItems.length,
                items: []
              });
              setAddModalOpen(true);
            }}
            size="sm"
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            إضافة
          </Button>
        </div>

        <div className="space-y-2">
          {menus.map((item) => renderNavItem(item))}
          {menus.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              لا توجد قوائم حالياً
            </div>
          )}
        </div>
      </Card>

      {/* Edit/Add Modal */}
      <NavItemModal
        open={editModalOpen || addModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditModalOpen(false);
            setAddModalOpen(false);
            setCurrentItem(null);
          }
        }}
        item={currentItem}
        onSave={handleSaveItem}
        isEdit={editModalOpen}
        menus={menus}
      />
    </div>
  );
}

// Modal Component
interface NavItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: NavItem | null;
  onSave: (item: NavItem) => void;
  isEdit: boolean;
  menus: NavItem[]; // List of available menus
}

function NavItemModal({ open, onOpenChange, item, onSave, isEdit, menus }: NavItemModalProps) {
  const [formData, setFormData] = useState<NavItem>(
    item || { title: { ar: '', en: '' }, dropdown: false, order: 0, items: [] }
  );
  const [subItems, setSubItems] = useState<NavSubItem[]>(item?.items || []);

  useEffect(() => {
    if (item) {
      setFormData(item);
      setSubItems(item.items || []);
    }
  }, [item]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...formData, items: formData.dropdown ? subItems : [] });
  };

  const addSubItem = () => {
    setSubItems([...subItems, { title: { ar: '', en: '' } }]);
  };

  const updateSubItem = (index: number, lang: 'ar' | 'en', value: string) => {
    const updated = [...subItems];
    updated[index] = { 
      ...updated[index], 
      title: { ...updated[index].title, [lang]: value }
    };
    setSubItems(updated);
  };

  const removeSubItem = (index: number) => {
    setSubItems(subItems.filter((_, i) => i !== index));
  };

  // If it's a menu (dropdown), show simplified form with only title
  if (formData.dropdown) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'تعديل القائمة' : 'إضافة قائمة جديدة'}</DialogTitle>
            <DialogDescription>
              اسم القائمة الرئيسية بالعربية والإنجليزية
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <Label className="text-base font-semibold">اسم القائمة / Menu Name</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="title-ar"
                    value={formData.title.ar}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                    placeholder="مثال: الجمعية"
                    required
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="title-en"
                    value={formData.title.en}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                    placeholder="Example: About"
                    required
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                إلغاء
              </Button>
              <Button type="submit">
                {isEdit ? 'حفظ التغييرات' : 'إضافة'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // For regular items, show simplified form with menu selection
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'تعديل العنصر' : 'إضافة عنصر جديد'}</DialogTitle>
          <DialogDescription>
            معلومات العنصر والقائمة التي ينتمي إليها بالعربية والإنجليزية
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <Label className="text-base font-semibold">اسم العنصر / Item Name</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Input
                  id="title-ar"
                  value={formData.title.ar}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                  placeholder="مثال: من نحن"
                  required
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
                <Input
                  id="title-en"
                  value={formData.title.en}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                  placeholder="Example: About Us"
                  required
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="parentMenu">القائمة الأم / Parent Menu</Label>
            <Select
              value={formData.parentMenu || 'none'}
              onValueChange={(value) => setFormData({ ...formData, parentMenu: value === 'none' ? undefined : value })}
            >
              <SelectTrigger id="parentMenu" dir="rtl">
                <SelectValue placeholder="اختر القائمة" />
              </SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="none">لا ينتمي لقائمة</SelectItem>
                {menus.map((menu) => (
                  <SelectItem key={menu.id} value={menu.id || ''}>
                    {typeof menu.title === 'string' ? menu.title : menu.title.ar}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit">
              {isEdit ? 'حفظ التغييرات' : 'إضافة'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

